// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasProduct, withProduct } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductCardSelections } from "@insite/content-library/Widgets/ProductList/ProductCardSelections";
import Checkbox from "@insite/mobius/Checkbox/Checkbox";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import XngQuantityBreakPrice from "@rugby/App/Components/CustomQuantityBreakPrice/XngQuantityBreakPrice";
import ProductAddToListLink from "@rugby/Overrides/Components/ProductAddToListLink";
import ProductListActions, { ProductListActionsStyles } from "@rugby/Overrides/Widgets/ProductList/ProductListActions";
import ProductListProductImage from "@rugby/Overrides/Widgets/ProductList/ProductListProductImage";
import ProductListProductInformation from "@rugby/Overrides/Widgets/ProductList/ProductListProductInformation";
import customProductCardStyles from "@rugby/Overrides/Widgets/ProductList/styles/ProductListProductCard.styles";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

// <XNG-Changes>
type Props = ReturnType<typeof mapStateToProps> & ProductCardSelections & HasProduct;

const mapStateToProps = (state: ApplicationState) => ({
    settingsCollection: getSettingsCollection(state),
});
// </XNG-Changes>

export interface ProductListProductCardStyles {
    gridContainer?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridContainer?: GridContainerProps;
    infoGridItem?: GridItemProps;
    actionsGridItem?: GridItemProps;
    sponsoredText?: TypographyPresentationProps;
}

export const productCardStyles: ProductListProductCardStyles = {
    gridContainer: {
        gap: 0,
        css: css`
            padding: 30px 0;
            min-width: 320px; /* prevent overlap with second column */
        `,
    },
    leftColumnGridItem: {
        width: [4, 4, 4, 3, 3],
        css: css`
            padding-right: 20px;
        `,
    },
    rightColumnGridItem: {
        width: [8, 8, 8, 9, 9],
    },
    infoGridItem: {
        width: [12, 12, 12, 7, 7],
    },
    actionsGridItem: {
        width: [12, 12, 12, 5, 5],
    },
    sponsoredText: {
        css: css`
            margin-bottom: 5px;
            font-size: 0.9em;
            opacity: 0.5;
        `,
    },
};

const styles = customProductCardStyles;

const ProductListProductCard: FC<Props> = props => {
    const hasAttribute =
        props.showAttributes && props.product?.attributeTypes && props.product?.attributeTypes?.length > 0;

    const { product, productInfo } = props;

    const productContext = {
        product,
        productInfo,
    };
    const renderCompareAndAddToListContainer = (
        <StyledWrapper {...styles.subInformationWrapper.compareAndAddToCartContainer.wrapper}>
            <StyledWrapper>
                {props.product.isSponsored && (
                    <Typography {...productCardStyles.sponsoredText}>{translate("Sponsored")}</Typography>
                )}
                <ProductListProductImage
                    showImage={false}
                    showCompare={props.showCompare && !props.product.isVariantParent}
                    extendedStyles={styles.subInformationWrapper.compareAndAddToCartContainer}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                />
            </StyledWrapper>
            <StyledWrapper>
                {props.showAddToList && (
                    <ProductAddToListLink
                        data-test-selector={`actionsAddToList${props.product.id}`}
                        extendedStyles={styles.subInformationWrapper.compareAndAddToCartContainer.addToListLink}
                    />
                )}
            </StyledWrapper>
        </StyledWrapper>
    );

    return (
        <StyledWrapper {...styles.container.wrapper} data-test-selector={`productListProductCard${props.product.id}`}>
            <StyledWrapper
                {...styles.container.infoWrapper}
                {...({ isParent: props.product.isVariantParent } as any)}
                data-test-selector="productListProductCard_infoSide"
            >
                <ProductListProductImage
                    showImage={props.showImage}
                    showCompare={false}
                    extendedStyles={styles.productListProductImage}
                    showImageBadge={props.showImageBadges}
                    showTextBadge={props.showTextBadges}
                />

                <StyledWrapper {...styles.subInformationWrapper.wrapper} {...{ hasAttribute }}>
                    <ProductListProductInformation
                        showBrand={props.showBrand}
                        showTitle={props.showTitle}
                        showPartNumbers={props.showPartNumbers}
                        showAttributes={props.showAttributes}
                        showAvailability={false}
                        showMinPack={true}
                        showImageBadges={props.showImageBadges}
                        maxImageBadges={props.maximumNumberOfImageBadges}
                        showTextBadges={props.showTextBadges}
                        maxTextBadges={props.maximumNumberOfTextBadges}
                    />
                    <XngQuantityBreakPrice product={productContext} />
                </StyledWrapper>
            </StyledWrapper>
            <StyledWrapper {...styles.container.actionWrapper} data-test-selector="productListProductCard_actionSide">
                <ProductListActions
                    showAvailability={props.showAvailability}
                    showPrice={props.showPrice}
                    showAddToList={false}
                    extendedStyles={styles.subActionWrapper.productListActions}
                />
                {renderCompareAndAddToListContainer}
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(withProduct(ProductListProductCard));
